import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Activity,
  AlertCircle,
  AlertTriangle,
  BarChart3,
  Bell,
  ChevronRight,
  FileText,
  Sparkles,
  Upload,
} from 'lucide-react';
import SignalStrip from '../../components/SignalStrip';
import StatusBadge from '../../components/StatusBadge';
import { daysBetween, fmtDateShort, fmtMoney, relativeTime } from '../../utils/format';
import { getDashboardGreeting } from './useDashboardData';

// Two surfaces here deep-link into /contracts?filter=deadline, and that list reads its
// window off ?deadline=<days>. Each link has to carry the window of the number printed
// above it — a bare link inherits the list's 60-day default and opens a different set
// than the one it counted. Both values must stay inside DEADLINE_WINDOW_OPTIONS
// (useContractsListData) or parseDeadlineWindow coerces them back to 60.

// The "Deadlines · 30d" stat tile shows deadlinesUpcoming.in30Days — a 30-day count,
// so its link opens a 30-day list (doc-144).
export const STAT_TILE_DEADLINE_WINDOW_DAYS = 30;

// expiringSoon/noticeSoon come off /api/dashboard as 90-day sets (routes/dashboard.js),
// and the card lists them unfiltered — so its "View all" has to open the list on the
// SAME 90-day window. It used to drop into the list's hardcoded 60 days, which hid
// every 61-90d row the card was showing right above the link (doc-140).
export const UPCOMING_DEADLINES_WINDOW_DAYS = 90;

// The card sits in a fixed-height dashboard row, so its body renders at most this many
// rows and the rest are DISCLOSED rather than shown (doc-212). Raising this is not the
// fix for a long list — the "View all" link above is.
const UPCOMING_DEADLINES_ROW_CAP = 8;

// The portfolio card is the deadlines card's grid sibling, so whichever is taller sets
// the row height and the other one carries the dead space. The deadlines card is bounded
// (8 rows); the type list was not, so past ~14 types the portfolio drove the row and the
// dead zone moved into the deadlines card — at 20 types the row ran past the fold on a
// 740px window (doc-237).
//
// Derivation, measured on the live app at 1440/1280/1101 (identical at all three, and
// identical in both themes): heat row 37px, portfolio card chrome 76px, the disclosure
// footer below 45px, and the deadlines card at its own cap plus its doc-212 footer is
// 669px. So 76 + 37N + 45 <= 669 gives N <= 14.5. At 14 the portfolio is 639px and the
// deadlines card still sets the row; 15 lands at 676px and hands the row straight back.
// The margin is 30px — one row less than a heat row, so this is TIGHT by construction.
//
// COUPLED to UPCOMING_DEADLINES_ROW_CAP: it is sized against that card at FULL
// extension, so if the 8 above ever changes this number must be re-derived from the same
// arithmetic, not left standing.
const PORTFOLIO_TYPE_ROW_CAP = 14;

// Two DIFFERENT numbers, and the dashboard prints both (doc-158).
//
// One contract can hold an expiration date AND an auto-renewal-notice date
// inside the same window, so:
//   deadlines30 (deadlinesUpcoming.in30Days) is COUNT(DISTINCT id) = CONTRACTS
//   expiring30 + noticeUpcoming30 are one COUNT(*) per date column = DEADLINES
// routes/dashboard.js builds the contract count by unioning the exact predicates
// of the two column counts, so it is 0 if and only if both of them are — but it
// is SMALLER than their sum whenever one contract holds both dates.
//
// Printing them together unlabelled invited arithmetic that fails: "4 deadlines
// (2 expiring, 3 notice)" — 2 + 3 = 5. The headline stays CONTRACT-shaped,
// because that is the number the tile's own href opens (doc-144) and the
// Upcoming-deadlines card chip counts (doc-145); every deadline-shaped number is
// LABELLED as a deadline count, so the breakdown cannot be read as a partition
// of the headline. One helper for both sites so the two cannot drift apart again.
export function deadlineBreakdown(expiring30, noticeUpcoming30, separator) {
  const expiring = expiring30 || 0;
  const notice = noticeUpcoming30 || 0;
  const total = expiring + notice;
  return `${total} ${total === 1 ? 'deadline' : 'deadlines'}: ${expiring} expiring${separator}${notice} notice`;
}

export function DashboardLoadingState() {
  return (
    <div className="canvas-inner animate-fade-in">
      <div className="page-header">
        <div style={{ flex: 1 }}>
          <div className="skeleton" style={{ height: 16, width: 140, marginBottom: 10, borderRadius: 4 }} />
          <div className="skeleton" style={{ height: 28, width: 280, marginBottom: 8, borderRadius: 4 }} />
          <div className="skeleton" style={{ height: 16, width: 320, borderRadius: 4 }} />
        </div>
      </div>
      {/* Mirrors the loaded shape (doc-155): the 4-tile action row plus the
          portfolio strip under it, so the page does not visibly re-flow from a
          bare 4-up grid into row-plus-strip the moment the payload lands. */}
      <div className="stat-row" style={{ '--stat-cols': 4 }}>
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="stat-card">
            <div className="skeleton" style={{ height: 60 }} />
          </div>
        ))}
      </div>
      <div className="card fact-strip portfolio">
        {[0, 1].map((i) => (
          <div key={i} className="fact-cell">
            <div className="skeleton" style={{ height: 34 }} />
          </div>
        ))}
      </div>
    </div>
  );
}

export function DashboardErrorState({ error }) {
  return (
    <div className="canvas-inner">
      <div className="callout red">
        <div className="callout-icon">
          <AlertTriangle className="w-4 h-4" />
        </div>
        <div className="callout-body">
          <strong>Failed to load dashboard.</strong>
          <div style={{ marginTop: 4, fontSize: 12 }}>{error}</div>
        </div>
      </div>
    </div>
  );
}

export function DashboardHeader({
  canWriteContracts,
  deadlines30,
  expiring30,
  firstName,
  noticeUpcoming30,
  todayLabel,
}) {
  return (
    <div className="page-header">
      <div style={{ minWidth: 0 }}>
        <div className="page-eyebrow">Dashboard · {todayLabel}</div>
        <h1 className="page-title">{getDashboardGreeting()}, {firstName}.</h1>
        <div className="page-subtitle">
          {deadlines30 > 0
            ? (
              <>
                You have{' '}
                <strong style={{ color: 'var(--text-primary)', fontWeight: 500 }}>
                  {deadlines30} {deadlines30 === 1 ? 'contract' : 'contracts'}
                </strong>
                {' '}with {expiring30 + noticeUpcoming30 === 1 ? 'a deadline' : 'deadlines'}
                {' '}in the next 30 days{' '}
                ({deadlineBreakdown(expiring30, noticeUpcoming30, ', ')}).
              </>
            )
            : <>No deadlines in the next 30 days.</>}
        </div>
      </div>
      <div className="row" style={{ gap: 8 }}>
        {canWriteContracts && (
          <Link to="/contracts/upload" className="btn primary">
            <Upload className="w-4 h-4" />
            Upload contract
          </Link>
        )}
      </div>
    </div>
  );
}

export function DashboardTopDeadlineAlert({
  canWriteContracts,
  dismissingId,
  dismissReminder,
  topDeadline,
}) {
  if (!topDeadline) return null;

  return (
    <div className="alert-stack">
      <div className={`alert-row alert-row--${topDeadline.kind === 'notice' ? 'red' : 'yellow'}`}>
        <span className="alert-icon">
          {topDeadline.kind === 'notice'
            ? <AlertCircle className="w-4 h-4" />
            : <AlertTriangle className="w-4 h-4" />}
        </span>
        <div className="alert-body">
          <strong>{topDeadline.contract.title}</strong>
          {topDeadline.kind === 'notice' ? ' auto-renew notice closes in ' : ' expires in '}
          <span className="mono">{topDeadline.days}</span> day{topDeadline.days === 1 ? '' : 's'}
          <span className="muted"> · {topDeadline.contract.counterparty || 'Unknown counterparty'}</span>
        </div>
        <div className="alert-actions">
          <Link to={`/contracts/${topDeadline.contract.id}`} className="btn outline sm">
            Open
          </Link>
          {canWriteContracts && (
            <button
              type="button"
              className="btn ghost sm"
              onClick={() => dismissReminder(topDeadline.contract.id, topDeadline.kind === 'notice' ? 'notice' : 'renewal')}
              disabled={dismissingId === topDeadline.contract.id}
              title="Dismiss this reminder. It'll come back if the underlying date shifts."
            >
              Dismiss
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// The info-vs-action rule for the stat row, stated ONCE (doc-155).
//
// The row contains only work legal owes, and every tile in it carries exactly
// one state chip at EVERY count — including zero. Three values, meaning the
// same thing on every tile: the chip describes the condition of that tile's own
// number, and the label (not the chip) says which queue it is.
//
// This replaces a per-tile ad-hoc scheme where the chip was gated on count > 0
// (so a zero action tile shed its chip and looked identical to an info tile),
// where the green "Live" chip sat on an INFORMATIONAL tile and inverted the
// signal, and where two different tiles both said the literal word "Action" so
// the chip disambiguated nothing. Informational numbers are no longer tiles at
// all — they live in the portfolio strip below, which has no chips.
function stateChip(value, { dated = false } = {}) {
  if (!(value > 0)) return { cls: 'chip neutral sq', text: 'Clear' };
  // Deadlines is the only dated queue, so it is the only one that can be late.
  if (dated) return { cls: 'chip red sq', text: 'Soon' };
  return { cls: 'chip yellow sq', text: 'Waiting' };
}

// `chip neutral sq` and not `chip gray sq`: gray is --text-secondary on
// --color-gray-100 = 3.66:1 in light mode, below AA. Neutral is --text-primary
// on the same background = 15.25:1 light / 13.49:1 dark.

function StatTile({ chip, label, sub, to, value }) {
  return (
    <Link to={to} className="stat-card">
      <div className="stat-label">{label}</div>
      {/* The chip is in flow here, opposite the number — not absolutely
          positioned into the label row. See .stat-valrow in components.css. */}
      <div className="stat-valrow">
        <div className="stat-value">{value}</div>
        <span className={chip.cls}>{chip.text}</span>
      </div>
      <div className="stat-sub">{sub}</div>
    </Link>
  );
}

export function DashboardStatsGrid({
  activeCount,
  deadlines30,
  draftCount,
  expiring30,
  inReviewCount,
  noticeUpcoming30,
  reviewQueue,
  totalContracts,
  tprmPipeline,
}) {
  // Every tile deep-links to the list it counted, pre-filtered, so a number you
  // care about is one click from the rows behind it. The filter values are the
  // ones the destination lists already read: `filter` on /contracts is
  // single-valued (expiring|notice|deadline), ?status=open on /reviews is the
  // service's not-yet-decided set, and ?status=pipeline on /tprm is
  // submitted+under_review — each matching its tile's number exactly.
  //
  // An action tile's LABEL is <domain> + <state>, where the domain is the nav
  // surface the tile opens (doc-155). "In review" (contracts awaiting legal
  // sign-off) and "Review queue" (documents awaiting a decision) were different
  // domains wearing near-identical English and were genuinely indistinguishable.
  // "Deadlines · 30d" keeps its name: nothing else in Docket has deadlines.
  //
  // Built as an array, not inline JSX, so --stat-cols below can be the count of
  // what ACTUALLY renders — the review-queue and TPRM tiles are payload-gated.
  const tiles = [
    {
      key: 'deadlines',
      to: `/contracts?filter=deadline&deadline=${STAT_TILE_DEADLINE_WINDOW_DAYS}`,
      label: `Deadlines · ${STAT_TILE_DEADLINE_WINDOW_DAYS}d`,
      value: deadlines30,
      // The sub has to describe the number printed above it, and that number is
      // CONTRACT-shaped — so the breakdown says out loud that it is counting
      // DEADLINES (doc-158). Gated on the tile's own value rather than on the two
      // column counts: identical in production (see deadlineBreakdown), but it
      // makes a 0 tile unable to render a non-empty breakdown.
      sub: deadlines30 > 0
        ? deadlineBreakdown(expiring30, noticeUpcoming30, ' · ')
        : `None in the next ${STAT_TILE_DEADLINE_WINDOW_DAYS} days`,
      chip: stateChip(deadlines30, { dated: true }),
    },
    {
      key: 'in-review',
      to: '/contracts?status=in_review',
      label: 'Contracts in review',
      value: inReviewCount,
      sub: 'Awaiting legal sign-off',
      chip: stateChip(inReviewCount),
    },
  ];
  if (reviewQueue) {
    tiles.push({
      key: 'review-queue',
      to: '/reviews?status=open',
      label: 'Document reviews',
      value: reviewQueue.open,
      sub: reviewQueue.open > 0
        ? `${reviewQueue.pendingReview} awaiting legal · ${reviewQueue.changesRequested} changes requested`
        : 'No documents awaiting a decision',
      // Gated on .open, not .pendingReview: the chip has to describe the number
      // printed above it, and .open is what the tile shows.
      chip: stateChip(reviewQueue.open),
    });
  }
  if (tprmPipeline) {
    tiles.push({
      key: 'tprm',
      to: '/tprm?status=pipeline',
      label: 'TPRM requests',
      value: tprmPipeline.open,
      sub: tprmPipeline.open > 0
        ? `${tprmPipeline.submitted} submitted · ${tprmPipeline.underReview} under review`
        : 'No vendor requests in review',
      // Same correction as above: was gated on .submitted while showing .open.
      chip: stateChip(tprmPipeline.open),
    });
  }

  const activePct = Math.round(totalContracts > 0 ? (activeCount / totalContracts) * 100 : 0);

  return (
    <>
      <div className="stat-row" style={{ '--stat-cols': tiles.length }}>
        {tiles.map(({ key, ...tile }) => <StatTile key={key} {...tile} />)}
      </div>
      {/* Portfolio facts, demoted out of the action row (doc-155). These are
          things that are TRUE, not things anyone owes work on, so they are a
          different component rather than a tile you have to decode a badge on —
          and dropping them from the row is what makes it compose as one line.
          Both deep-links doc-139 shipped survive; they move to strip cells. */}
      <div className="card fact-strip portfolio">
        <Link to="/contracts" className="fact-cell">
          <div className="fact-label">Total contracts</div>
          <div className="fact-value">{totalContracts}</div>
          <div className="fact-sub">{activeCount} active · {draftCount} draft</div>
        </Link>
        <Link to="/contracts?status=active" className="fact-cell">
          <div className="fact-label">Active</div>
          <div className="fact-value">{activeCount}</div>
          {/* Percent of portfolio, unconditionally. This cell used to read
              "activeValue > 0 ? fmtMoney(activeValue) in play : N% of
              portfolio", and the money branch had never rendered for anyone:
              /api/dashboard has never emitted totalActiveValue, so activeValue
              was always 0 (doc-175). Money on the dashboard is a product
              decision nobody has made — contracts.contract_value is nullable
              and AI-extraction-sourced, and contracts.currency is free text
              with no FX source anywhere in the repo, so a bare SUM would mix
              USD/EUR/GBP and silently under-report. If that decision is ever
              made, the sum goes in backend/routes/dashboard.js FIRST (see
              doc-175 option A) and this cell changes with it — don't re-add a
              branch on a field the payload doesn't carry. */}
          <div className="fact-sub">{activePct}% of portfolio</div>
        </Link>
      </div>
    </>
  );
}

export function getUpcomingDeadlineItems(expiringSoon, noticeSoon) {
  return [
    ...expiringSoon.map((contract) => ({
      kind: 'expiring',
      contract,
      date: contract.expirationDate,
      days: daysBetween(new Date(), contract.expirationDate),
    })),
    ...noticeSoon.map((contract) => ({
      kind: 'notice',
      contract,
      date: contract.autoRenewalNoticeDate,
      days: daysBetween(new Date(), contract.autoRenewalNoticeDate),
    })),
  ].sort((a, b) => new Date(a.date) - new Date(b.date));
}

// The card lists one row per DEADLINE, so a contract with both an expiration_date and
// an auto_renewal_notice_date inside the window appears twice — an ordinary shape for
// an auto-renewing contract whose notice date falls before it expires. Everything the
// chip is read against counts CONTRACTS, not deadlines: the "View all" list ORs both
// dates into one predicate per row (contract-read-service.js) and the Deadlines tile
// uses COUNT(DISTINCT id) (routes/dashboard.js). Counting items.length made the chip
// over-report against both (doc-145) — 7 next to a list of 6. Count contracts here and
// keep listing every deadline below.
export function countUpcomingDeadlineContracts(items) {
  return new Set(items.map((item) => item.contract.id)).size;
}

export function DashboardDeadlinePortfolioGrid({ expiringSoon, noticeSoon, typeHeat }) {
  const items = getUpcomingDeadlineItems(expiringSoon, noticeSoon);

  // The two-column split lives in CSS (.dash-split), not in an inline style: an
  // inline style cannot carry a media query, so while these tracks were inline
  // there was literally no mechanism for this row to respond to viewport, and
  // the portfolio card kept shrinking until its bar hit 0px (doc-210).
  return (
    <div className="dash-split">
      <UpcomingDeadlinesCard items={items} />
      <PortfolioByTypeCard typeHeat={typeHeat} />
    </div>
  );
}

function UpcomingDeadlinesCard({ items }) {
  const contractCount = countUpcomingDeadlineContracts(items);
  const visible = items.slice(0, UPCOMING_DEADLINES_ROW_CAP);
  const truncated = items.length > visible.length;
  // Derived from the card's OWN items, not the 30-day payload counts the tile and hero
  // pass to deadlineBreakdown — this list is a 90-day window
  // (UPCOMING_DEADLINES_WINDOW_DAYS), so a 30d breakdown printed here would be a fresh
  // unit bug. Counts every item, not the 8 the body renders: the sub-line states the
  // true 90-day total, and the footer below reconciles it with the rows on screen.
  const expiringCount = items.filter((item) => item.kind === 'expiring').length;
  const noticeCount = items.filter((item) => item.kind === 'notice').length;

  return (
    <div className="card">
      <div className="card-header compact">
        {/* The chip is CONTRACT-shaped (doc-145) while the rows below are one per
            DEADLINE, so a dual-deadline contract puts N directly above N+1 rows under a
            heading that says "deadlines". Both numbers name their unit in visible text
            — doc-158's rule, applied at the one site its diff missed (doc-170). */}
        <div style={{ minWidth: 0 }}>
          <div className="row" style={{ gap: 8 }}>
            <Bell className="w-4 h-4" style={{ color: 'var(--icon-secondary)' }} />
            <span className="card-title">Upcoming deadlines</span>
            <span className="chip gray" style={{ marginLeft: 4 }}>
              {contractCount} {contractCount === 1 ? 'contract' : 'contracts'}
            </span>
          </div>
          {items.length > 0 && (
            <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>
              {deadlineBreakdown(expiringCount, noticeCount, ' · ')}
            </div>
          )}
        </div>
        <Link
          to={`/contracts?filter=deadline&deadline=${UPCOMING_DEADLINES_WINDOW_DAYS}`}
          className="muted"
          style={{ fontSize: 12 }}
        >
          View all →
        </Link>
      </div>
      <div className="card-body flush">
        {items.length === 0 ? (
          <div style={{ padding: '36px 20px', textAlign: 'center' }}>
            <div className="muted" style={{ fontSize: 13 }}>
              No upcoming deadlines.
            </div>
          </div>
        ) : (
          visible.map((item, index, list) => (
            <UpcomingDeadlineRow
              key={`${item.kind}-${item.contract.id}`}
              item={item}
              isLast={index === list.length - 1}
            />
          ))
        )}
        {/* The house truncation-disclosure pattern (doc-191/doc-212): a muted footer at
            the END of the list saying how much of the header's total is on screen. The
            header sub-line's 90-day deadline count was the ONLY signal that anything was
            dropped, so a reader counting rows got 8 and matched neither printed number.
            The upcoming-list variant of the wording, not the table one, because this is a
            soonest-first list with no expander — the other rows are behind "View all".
            isLast already zeroes the 8th row's divider (map's third arg is the SLICED
            array), so this line's own top border is the single seam divider. .card-body
            .flush has padding 0, so the 18px inset lines the footer up with the rows. */}
        {truncated && (
          <div
            className="row"
            style={{
              padding: '10px 18px',
              borderTop: '0.5px solid var(--border-primary)',
              gap: 10,
              fontSize: 12,
              color: 'var(--text-secondary)',
            }}
          >
            <span>Showing the next {visible.length} of {items.length}</span>
          </div>
        )}
      </div>
    </div>
  );
}

function UpcomingDeadlineRow({ isLast, item }) {
  const contract = item.contract;
  const days = item.days;
  const isUrgent = days != null && days <= 14;

  return (
    <Link
      to={`/contracts/${contract.id}`}
      style={{
        display: 'grid',
        // The kind-badge track has a floor wide enough for the WIDEST kind label
        // ('EXPIRING', measured at 62.6px) so every row title starts at the same
        // x — an auto track sizes to the badge text, which left the shorter
        // 'NOTICE' row's title 10.4px out of line (doc-223). Adding a new kind
        // means re-measuring this floor; minmax keeps a longer label ragged
        // rather than clipped.
        gridTemplateColumns: 'minmax(64px, auto) 1fr auto auto',
        gap: 14,
        alignItems: 'center',
        padding: '12px 18px',
        borderBottom: isLast ? 0 : '0.5px solid var(--border-primary)',
        color: 'inherit',
        textDecoration: 'none',
      }}
    >
      <span
        className={`chip sq ${item.kind === 'expiring' ? 'yellow' : 'red'}`}
        style={{ justifySelf: 'start' }}
      >
        {item.kind === 'expiring' ? 'Expiring' : 'Notice'}
      </span>
      <div className="stack-sm" style={{ minWidth: 0 }}>
        <div className="ellipsis" style={{ fontSize: 13, fontWeight: 450 }}>{contract.title}</div>
        <div className="ellipsis muted" style={{ fontSize: 11 }}>
          {contract.shortName && (
            <>
              <span className="mono">{contract.shortName}</span>
              <span style={{ margin: '0 5px', opacity: 0.4 }}>·</span>
            </>
          )}
          {contract.counterparty || '—'}
          {contract.internalOwner && <> · {contract.internalOwner}</>}
        </div>
      </div>
      <span
        className="chip sq"
        style={{
          background: isUrgent ? 'var(--surface-red)' : 'var(--surface-alpha-primary)',
          color: isUrgent ? 'var(--text-critical)' : 'var(--text-secondary)',
        }}
      >
        {days == null ? '—' : days <= 0 ? 'today' : `${days}d`}
      </span>
      <ChevronRight className="w-3 h-3" style={{ opacity: 0.3 }} />
    </Link>
  );
}

function PortfolioByTypeCard({ typeHeat }) {
  // This card is a grid sibling of the (usually taller) deadlines card, so it
  // gets stretched to the row height. Column-flex the card and let the list
  // body grow into whatever height that leaves, instead of capping the list at
  // a fixed px height and leaving a dead zone under it (doc-185). minHeight 0
  // is load-bearing: without it the body refuses to shrink below its content
  // and the scrollbar never appears when the list genuinely does overflow.
  //
  // The body still carries NO height ceiling (doc-211, and it stays true): a ceiling
  // smaller than the space the row actually leaves recreates the exact defect this card
  // was filed for, and a viewport-relative one (an earlier 60vh) is guaranteed to be
  // that on any laptop, because the constraint it stands in for is the SIBLING card's
  // height, which has nothing to do with vh.
  //
  // What bounds the list instead is CONTENT (doc-237): a named row cap plus the doc-191
  // disclosure footer, mirroring the shape the sibling card already ships for its own
  // truncation (doc-212). The cap is sized so the portfolio can never make the row
  // taller than the deadlines card at full extension — so neither card carries a dead
  // zone, no inner scrollbar can return at any viewport, and the long tail is one click
  // away rather than dropped silently.
  const [expanded, setExpanded] = useState(false);
  const capped = typeHeat.entries.length > PORTFOLIO_TYPE_ROW_CAP;
  const visible = capped && !expanded
    ? typeHeat.entries.slice(0, PORTFOLIO_TYPE_ROW_CAP)
    : typeHeat.entries;

  return (
    <div className="card" style={{ display: 'flex', flexDirection: 'column' }}>
      <div className="card-header compact">
        <div className="row" style={{ gap: 8 }}>
          <BarChart3 className="w-4 h-4" style={{ color: 'var(--icon-secondary)' }} />
          <span className="card-title">Portfolio by type</span>
        </div>
      </div>
      <div
        className="card-body"
        style={{
          paddingTop: 6,
          flex: '1 1 auto',
          minHeight: 0,
          overflowY: 'auto',
        }}
      >
        {typeHeat.entries.length === 0 ? (
          <div style={{ padding: '16px 0', textAlign: 'center' }}>
            <div className="muted" style={{ fontSize: 13 }}>No contracts yet.</div>
          </div>
        ) : visible.map(([type, count]) => (
          <Link
            key={type}
            to={`/contracts?type=${encodeURIComponent(type)}`}
            className="heat-row"
            style={{ textDecoration: 'none', color: 'inherit' }}
          >
            <div className="ellipsis name" title={type}>{type}</div>
            <div className="bar">
              <div className="fill" style={{ width: `${(count / typeHeat.max) * 100}%` }} />
            </div>
            <div className="val">{count}</div>
          </Link>
        ))}
      </div>
      {/* Sibling of .card-body, NOT a child of it, and that placement is load-bearing
          twice over: the body carries 24px of horizontal padding, so a footer inside it
          would inset the divider and lose the full-bleed seam the sibling card's footer
          has; and pinning the footer to the bottom of the column-flex card turns
          whatever slack the row leaves into the card's own bottom chrome instead of a
          ragged gap under the last heat row.

          The expander (doc-191's shape) rather than a "View all" link, deliberately:
          per-type counts exist NOWHERE else in Docket (/contracts/facets returns no
          types, and the list's Type filter is populated from the canonical enum), so a
          legacy free-text type is reachable only through this card's own deep link —
          and a count-sorted cut drops the smallest types first, which is exactly where
          those legacy values live. Expanding refetches nothing: the full array is
          already in the payload.

          "types" is in the collapsed string because this row prints two numbers next to
          a list whose rows are themselves counts — every number names its unit in
          visible text (doc-158 / doc-170). */}
      {capped && (
        <div
          className="row"
          style={{
            flex: '0 0 auto',
            padding: '10px 24px',
            borderTop: '0.5px solid var(--border-primary)',
            gap: 10,
            fontSize: 12,
            color: 'var(--text-secondary)',
          }}
        >
          <span>
            {expanded
              ? `Showing all ${typeHeat.entries.length} types`
              : `Showing ${visible.length} of ${typeHeat.entries.length} types`}
          </span>
          <button
            type="button"
            className="btn ghost xs"
            onClick={() => setExpanded((value) => !value)}
            aria-expanded={expanded}
          >
            {expanded ? 'Show fewer' : 'Show all'}
          </button>
        </div>
      )}
    </div>
  );
}

export function DashboardTeamActivity({ canWriteContracts, recentActivity }) {
  if (!canWriteContracts || recentActivity.length === 0) return null;

  return (
    <div className="card" style={{ marginBottom: 20 }}>
      <div className="card-header compact">
        <div className="row" style={{ gap: 8 }}>
          <Activity className="w-4 h-4" style={{ color: 'var(--icon-secondary)' }} />
          <span className="card-title">Team activity</span>
          <span className="muted" style={{ fontSize: 11 }}>last 14 days</span>
        </div>
      </div>
      <div className="card-body flush">
        {recentActivity.slice(0, 8).map((event, index) => (
          <ActivityRow key={`${event.kind}-${event.contractId}-${event.timestamp}-${index}`} event={event} />
        ))}
      </div>
    </div>
  );
}

export function DashboardRecentContracts({ canWriteContracts, recent }) {
  const navigate = useNavigate();

  return (
    <div className="card">
      <div className="card-header compact">
        <div className="row" style={{ gap: 8 }}>
          <Sparkles className="w-4 h-4" style={{ color: 'var(--icon-secondary)' }} />
          <span className="card-title">Recently updated</span>
        </div>
        <Link to="/contracts" className="muted" style={{ fontSize: 12 }}>View all →</Link>
      </div>
      <div className="card-body flush">
        {recent.length === 0 ? (
          <div style={{ padding: '48px 20px', textAlign: 'center' }}>
            <div className="muted" style={{ fontSize: 13, marginBottom: 12 }}>No contracts yet.</div>
            {canWriteContracts && (
              <Link to="/contracts/upload" className="btn outline sm">
                <Upload className="w-3.5 h-3.5" />
                Upload your first contract
              </Link>
            )}
          </div>
        ) : (
          <table className="tbl">
            <tbody>
              {recent.slice(0, 6).map((contract) => (
                <tr
                  key={contract.id}
                  onClick={() => navigate(`/contracts/${contract.id}`)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      navigate(`/contracts/${contract.id}`);
                    }
                  }}
                  role="button"
                  tabIndex={0}
                >
                  <td style={{ width: 30 }}><SignalStrip contract={contract} /></td>
                  <td>
                    <div style={{ fontWeight: 450, fontSize: 13 }}>{contract.title}</div>
                    <div className="muted" style={{ fontSize: 11, marginTop: 2 }}>
                      {contract.shortName && (
                        <>
                          <span className="mono">{contract.shortName}</span>
                          <span style={{ margin: '0 5px', opacity: 0.4 }}>·</span>
                        </>
                      )}
                      {contract.counterparty || '—'}
                      {contract.contractType && <> · {contract.contractType}</>}
                    </div>
                  </td>
                  <td style={{ width: 130 }}><StatusBadge status={contract.status} /></td>
                  <td style={{ width: 110 }} className="mono tertiary" align="right">
                    {contract.contractValue ? fmtMoney(contract.contractValue, contract.currency) : <span style={{ opacity: 0.4 }}>—</span>}
                  </td>
                  <td style={{ width: 110 }} className="muted mono" align="right">
                    {contract.expirationDate ? fmtDateShort(contract.expirationDate) : ''}
                  </td>
                  <td style={{ width: 90 }} className="muted">{relativeTime(contract.updatedAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function ActivityRow({ event }) {
  const isNonLegal = event.userRole && !['legal', 'admin'].includes(event.userRole);
  const who = event.userName || event.userEmail || 'System';
  let verb = '';
  let icon = <FileText className="w-3.5 h-3.5" style={{ color: 'var(--icon-tertiary)' }} />;

  if (event.kind === 'created') {
    if (event.details?.isWizard) {
      verb = 'generated';
      icon = <Sparkles className="w-3.5 h-3.5" style={{ color: 'var(--text-purple)' }} />;
    } else {
      verb = 'created';
    }
  } else if (event.kind === 'file_uploaded') {
    const direction = event.details?.direction;
    if (direction === 'inbound') {
      verb = 'uploaded a counterparty markup to';
    } else if (event.details?.isExecuted) {
      verb = 'uploaded the executed version of';
    } else {
      verb = `uploaded ${event.details?.draftVersion || 'a draft'} to`;
    }
    icon = <Upload className="w-3.5 h-3.5" style={{ color: 'var(--icon-tertiary)' }} />;
  } else if (event.kind === 'status_changed') {
    verb = 'changed status of';
    icon = <Activity className="w-3.5 h-3.5" style={{ color: 'var(--icon-tertiary)' }} />;
  }

  const tail = event.kind === 'status_changed' && event.details?.newStatus
    ? ` → ${event.details.newStatus}`
    : '';

  return (
    <Link
      to={`/contracts/${event.contractId}`}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '10px 18px',
        borderLeft: isNonLegal ? '2px solid var(--color-purple-500)' : '2px solid transparent',
        borderBottom: '0.5px solid var(--border-primary)',
        color: 'inherit',
        textDecoration: 'none',
        background: isNonLegal ? 'var(--surface-purple)' : 'transparent',
      }}
    >
      <div style={{ flexShrink: 0 }}>{icon}</div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
          <span className="ellipsis" style={{ fontSize: 13, color: 'var(--text-primary)', flex: '0 1 auto', minWidth: 0 }}>
            <strong style={{ fontWeight: 500 }}>{who}</strong>{' '}
            <span className="muted">{verb}</span>{' '}
            <span style={{ color: 'var(--text-primary)' }}>{event.contractTitle || '(untitled)'}</span>
            {tail && <span className="muted mono" style={{ fontSize: 11 }}>{tail}</span>}
          </span>
          {isNonLegal && (
            <span className="chip purple sq" style={{ flexShrink: 0 }} title="Action taken by a non-legal user">
              self-serve
            </span>
          )}
        </div>
        <div className="muted" style={{ fontSize: 11, marginTop: 2 }}>
          {event.contractCounterparty || '—'}
          {event.details?.fileNotes && <> · {event.details.fileNotes}</>}
        </div>
      </div>
      <div className="muted mono" style={{ fontSize: 11, flexShrink: 0 }}>
        {relativeTime(event.timestamp)}
      </div>
    </Link>
  );
}
